import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, User } from "lucide-react";

export default function Users() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { email: "", password: "", name: "", role: "admin", is_active: true };
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setRows(Array.isArray(data) ? data : []);
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/users", form);
        toast.success("Eklendi");
      }
      setOpen(false); setEditing(null); setForm(empty);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Kullanıcıyı sil?")) return;
    await api.delete(`/users/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Kullanıcılar</h1>
          <p className="text-sm text-muted-foreground mt-1">Panel yönetici hesapları</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="users-add-btn">
          <Plus size={14} className="mr-1.5" /> Yeni Kullanıcı
        </Button>
      </div>

      <div className="border border-border bg-card rounded-md overflow-hidden">
        <table className="data-table" data-testid="users-table">
          <thead>
            <tr><th>E-posta</th><th>İsim</th><th>Rol</th><th>Durum</th><th>Oluşturulma</th><th className="text-right">İşlem</th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td className="font-mono text-xs flex items-center gap-2"><User size={13} className="text-muted-foreground" />{u.email}</td>
                <td>{u.name || "—"}</td>
                <td><span className="status-badge info">{u.role}</span></td>
                <td><span className={`status-badge ${u.is_active ? "ok" : "muted"}`}>{u.is_active ? "aktif" : "pasif"}</span></td>
                <td className="font-mono text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString("tr-TR")}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(u); setForm({ email: u.email, password: "", name: u.name, role: u.role, is_active: u.is_active }); setOpen(true); }}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(u.id)} className="text-red-400"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="user-form-email" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Parola{editing && " (değiştirmek için doldurun)"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">İsim</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="superadmin">superadmin</SelectItem>
                  <SelectItem value="admin">admin</SelectItem>
                  <SelectItem value="viewer">viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <span className="text-sm">Aktif</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="user-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
